import { useCallback, useState } from "react";

import { $count, $email, $ok } from "foundation-ts/commons";
import { UUID } from "foundation-ts/types";

import { ContactToImport, ImportViewPhase } from "@components/Import/types";
import { addUserContact, updateContact, uploadContacts } from "@services/users.service";
import { runWithConcurrency } from "@utils/runWithConcurrency";

export function useContactImport(connectedUserId?: UUID, concurrency = 5) {
    const [contacts, setContacts] = useState<ContactToImport[]>([]);
    const [phase, setPhase] = useState<ImportViewPhase>("preview");

    const updateContactItem = useCallback((id: UUID, patch: Partial<ContactToImport>) => {
        setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    }, []);

    const reset = useCallback(
        () => {
            setContacts([]);
            setPhase("preview");
        },
        []
    );

    const startImport = useCallback(
        async (allContacts: ContactToImport[], toImport: ContactToImport[]) => {
            setContacts(
                allContacts.map((c) =>
                    toImport.some((t) => t.id === c.id) ? { ...c, status: "importing" } : c
                )
            );
            setPhase("importing");
            await runWithConcurrency(
                toImport,
                async (contact) => {
                    try {
                        const phone = !$ok(contact.data.phone) && (contact.existedContact?.contact.phone) ? null : contact.data.phone?.trim();
                        const mail = $email(contact.data.email.toLowerCase());
                        if (!$ok(mail)) { throw 'Bad email'; }

                        if ($ok(contact.existedContact)) {
                            await updateContact(connectedUserId!, contact.existedContact!.apid!, { email: mail!, lastName: contact.data.lastName.toUpperCase().trim(), firstName: contact.data.firstName.trim().capitalize(), phone: phone });
                        } else {
                            const newContact = await addUserContact(connectedUserId!, { email: mail!, lastName: contact.data.lastName.toUpperCase().trim(), firstName: contact.data.firstName?.trim().capitalize(), phone: phone });
                            if (!$ok(newContact?.apid)) { throw 'Bad contact creation'; }
                        }
                        updateContactItem(contact.id, { status: "imported", note: undefined });
                    } catch (err) {
                        updateContactItem(contact.id, {
                            status: "failed",
                            note: err instanceof Error ? err.message : "Erreur inconnue",
                        });
                        throw err;
                    }
                },
                concurrency
            );
            setPhase("done");
        },
        [concurrency, updateContactItem]
    );

    const uploadContactsList = useCallback(
        async (toImport: ContactToImport[], timeout?: number): Promise<ImportStatus> => {
            let ret: ImportStatus = "waiting";
            try {
                setPhase("importing");
                const createdContacts = await uploadContacts(connectedUserId!, toImport, timeout);
                if ($count(createdContacts) > 0) {
                    const statusById = new Map<UUID, "imported" | "failed">();
                    toImport.forEach(t => {
                        const foundContact = createdContacts!.find(c => c.contact.email.toLowerCase() === t.data.email.toLowerCase());
                        statusById.set(t.id, $ok(foundContact) ? "imported" : "failed");
                    });
                    setContacts((prev) => prev.map((c) => statusById.has(c.id) ? { ...c, status: statusById.get(c.id)! } : c));
                    ret = "success";
                }
            } catch (e) {
                console.log(e);
                ret = "failed";
            }
            setPhase("done");
            return ret;
        },
        []
    );

    return { contacts, phase, reset, setContacts, startImport, uploadContactsList };
}

type ImportStatus = "waiting"|"failed"|"success";